import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { BottomNavigation } from "@/components/layout/BottomNavigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft,
  RefreshCw,
  CheckCircle,
  XCircle,
  Lightbulb,
  Target,
  Check,
  Calendar,
  ChevronLeft
} from "lucide-react";

export default function WeeklyResetPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [weeklyStats, setWeeklyStats] = useState({ completed: 0, missed: 0 });
  
  // Form inputs
  const [followedThrough, setFollowedThrough] = useState("");
  const [avoidedAction, setAvoidedAction] = useState("");
  const [patternObserved, setPatternObserved] = useState("");
  const [adjustment, setAdjustment] = useState("");
  const [commitment, setCommitment] = useState("");

  useEffect(() => {
    fetchWeeklyStats();
  }, []);

  const fetchWeeklyStats = async () => {
    try {
      // Fetch compass entries from the last 7 days
      const journalRes = await axios.get(`${API}/journal?tag=compass-checkin`, {
        withCredentials: true
      });
      
      const allCheckIns = journalRes.data || [];
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const weeklyCheckIns = allCheckIns.filter(e => {
        const entryDate = new Date(e.created_at);
        return entryDate >= sevenDaysAgo;
      });
      
      const completed = weeklyCheckIns.filter(e => e.tags?.includes('checkin-yes')).length;
      const missed = weeklyCheckIns.filter(e => e.tags?.includes('checkin-no')).length;
      
      setWeeklyStats({ completed, missed });
    } catch (error) {
      console.error("Failed to fetch weekly stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!commitment.trim()) {
      toast.error("Please enter your non-negotiable commitment for the week");
      return;
    }
    
    setSaving(true);
    try {
      // Store all reflections in a single journal entry with weekly-reset tag
      const reflectionContent = `
## Weekly Reset Reflection

### What I followed through on:
${followedThrough || "Not specified"}

### Where I avoided action:
${avoidedAction || "Not specified"}

### Pattern observed:
${patternObserved || "Not specified"}

### Adjustment for this week:
${adjustment || "Not specified"}

### Non-negotiable commitment:
${commitment}

---
**Stats:** ${weeklyStats.completed} actions completed, ${weeklyStats.missed} actions missed
      `.trim();
      
      await axios.post(`${API}/journal`, {
        title: "Weekly Reset",
        body: reflectionContent,
        tags: ["weekly-reset", "compass"],
        is_draft: false
      }, { withCredentials: true });
      
      toast.success("Weekly Reset saved. Stay committed.", {
        icon: <Check className="w-4 h-4" />,
        duration: 3000
      });
      
      // Navigate back to Compass
      navigate('/compass');
    } catch (error) {
      console.error("Failed to save Weekly Reset:", error);
      toast.error("Failed to save Weekly Reset");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-28">
        <main className="pt-6 pb-12 px-4 max-w-2xl mx-auto">
          <div className="flex items-center justify-center gap-3 text-foreground/60 h-[60vh]">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span>Loading...</span>
          </div>
        </main>
        <BottomNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-28">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="px-4 pt-6 pb-4">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/compass')}
              className="w-10 h-10 rounded-full bg-emerald-dark/10 flex items-center justify-center"
              data-testid="back-to-compass"
            >
              <ChevronLeft className="w-5 h-5 text-foreground" />
            </button>
            <div>
              <h1 className="text-2xl font-heading text-foreground">Weekly Reset</h1>
              <p className="text-foreground/50 text-sm">Review. Reflect. Commit.</p>
            </div>
          </div>
        </div>
      </div>
      
      <main className="px-4 py-6 max-w-2xl mx-auto">
        {/* Section 1: Review */}
        <section className="mb-6 animate-fade-in" style={{ animationDelay: "50ms" }} data-testid="review-section">
          <Card className="glass-card">
            <CardContent className="p-5 sm:p-6">
              <h2 className="text-lg font-heading text-white mb-4">Review</h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                  </div>
                  <p className="text-3xl font-heading text-emerald-400 mb-1" data-testid="completed-count">{weeklyStats.completed}</p>
                  <p className="text-emerald-400/70 text-sm">Actions Completed</p>
                </div>
                <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <XCircle className="w-5 h-5 text-orange-400" />
                  </div>
                  <p className="text-3xl font-heading text-orange-400 mb-1" data-testid="missed-count">{weeklyStats.missed}</p>
                  <p className="text-orange-400/70 text-sm">Actions Missed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Section 2: Reflection */}
        <section className="mb-6 animate-fade-in" style={{ animationDelay: "100ms" }} data-testid="reflection-section">
          <Card className="glass-card">
            <CardContent className="p-5 sm:p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-amber-400/20 flex items-center justify-center">
                  <Lightbulb className="w-5 h-5 text-amber-400" />
                </div>
                <h2 className="text-lg font-heading text-white">Reflection</h2>
              </div>
              
              <div className="space-y-5">
                <div>
                  <label className="text-white/70 text-sm block mb-2">What did you follow through on?</label>
                  <Textarea
                    value={followedThrough}
                    onChange={(e) => setFollowedThrough(e.target.value)}
                    placeholder="List specific actions you completed..."
                    className="min-h-[80px] bg-white/3 border-white/10 text-white placeholder:text-white/25 resize-none"
                    data-testid="followed-through-input"
                  />
                </div>
                
                <div>
                  <label className="text-white/70 text-sm block mb-2">Where did you avoid action?</label>
                  <Textarea
                    value={avoidedAction}
                    onChange={(e) => setAvoidedAction(e.target.value)}
                    placeholder="Be honest about what you avoided..."
                    className="min-h-[80px] bg-white/3 border-white/10 text-white placeholder:text-white/25 resize-none"
                    data-testid="avoided-action-input"
                  />
                </div>
                
                <div>
                  <label className="text-white/70 text-sm block mb-2">What pattern showed up most?</label>
                  <Textarea
                    value={patternObserved}
                    onChange={(e) => setPatternObserved(e.target.value)}
                    placeholder="Identify recurring behaviors or obstacles..."
                    className="min-h-[80px] bg-white/3 border-white/10 text-white placeholder:text-white/25 resize-none"
                    data-testid="pattern-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Section 3: Adjustment */}
        <section className="mb-6 animate-fade-in" style={{ animationDelay: "150ms" }} data-testid="adjustment-section">
          <Card className="glass-card">
            <CardContent className="p-5 sm:p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-blue-400/20 flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-blue-400" />
                </div>
                <h2 className="text-lg font-heading text-white">Adjustment</h2>
              </div>
              
              <div>
                <label className="text-white/70 text-sm block mb-2">What will you do differently this week?</label>
                <Textarea
                  value={adjustment}
                  onChange={(e) => setAdjustment(e.target.value)}
                  placeholder="Define one concrete change..."
                  className="min-h-[100px] bg-white/3 border-white/10 text-white placeholder:text-white/25 resize-none"
                  data-testid="adjustment-input"
                />
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Section 4: Commitment */}
        <section className="mb-8 animate-fade-in" style={{ animationDelay: "200ms" }} data-testid="commitment-section">
          <Card className="glass-card border-primary/20">
            <CardContent className="p-5 sm:p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                  <Target className="w-5 h-5 text-primary" />
                </div>
                <h2 className="text-lg font-heading text-white">Commitment</h2>
              </div>
              
              <div>
                <label className="text-white/70 text-sm block mb-2">What is one non-negotiable action this week?</label>
                <Textarea
                  value={commitment}
                  onChange={(e) => setCommitment(e.target.value)}
                  placeholder="State your commitment clearly..."
                  className="min-h-[100px] bg-white/3 border-white/10 text-white placeholder:text-white/25 resize-none"
                  data-testid="commitment-input"
                />
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Submit Button */}
        <div className="animate-fade-in" style={{ animationDelay: "250ms" }}>
          <Button 
            className="btn-primary w-full"
            onClick={handleSubmit}
            disabled={saving || !commitment.trim()}
            data-testid="submit-weekly-reset"
          >
            {saving ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
            ) : (
              <><Check className="w-4 h-4 mr-2" /> Commit & Save</>
            )}
          </Button>
        </div>

      </main>
      
      <BottomNavigation />
    </div>
  );
}
